/**
 * ExpensesMetrics
 *
 * Ad Spend & Expenses section of the analytics dashboard.
 * Displays daily/weekly/monthly ad spend and fixed expenses with profit analysis.
 */
import React from 'react';
import { 
  TrendingUp,
  Calendar,
  Activity,
  DollarSign,
  CreditCard,
  Minus,
  Target,
  Users,
  BarChart3
} from 'lucide-react';
import AnalyticsTooltip from './AnalyticsTooltip';

const ExpensesMetrics = ({ analyticsData, formatCurrency }) => {
  const totalMonthlyExpenses = (150 * 30) + 650;
  const currentMRR = analyticsData.summary?.currentMRR || 0;
  const monthlyProfit = currentMRR - totalMonthlyExpenses;
  const annualProfit = (currentMRR * 12) - ((150 * 365) + (650 * 12));

  return (
    <>
      {/* Ad Spend & Expenses */}
      <div className="mb-6">
        <h3 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-3">Ad Spend & Expenses</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
          {/* Daily Ad Spend */}
          <AnalyticsTooltip content="Daily Ad Spend - Fixed daily advertising budget of $150 for customer acquisition across all channels." id="daily-ad-spend-tooltip">
            <div className="bg-white dark:bg-transparent backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg p-4 cursor-help">
              <div className="flex items-center">
                <div className="p-2 bg-red-100 dark:bg-red-900/20 rounded-lg mr-3">
                  <TrendingUp className="h-5 w-5 text-red-600 dark:text-red-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Daily Ad Spend</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white">
                    {formatCurrency(150)}
                  </p>
                  <p className="text-xs text-red-600 dark:text-red-400">per day</p>
                </div>
              </div>
            </div>
          </AnalyticsTooltip>

          {/* Weekly Ad Spend */}
          <AnalyticsTooltip content="Weekly Ad Spend - Total advertising spend per week. Calculated as daily spend × 7 days." id="weekly-ad-spend-tooltip">
            <div className="bg-white dark:bg-transparent backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg p-4 cursor-help">
              <div className="flex items-center">
                <div className="p-2 bg-orange-100 dark:bg-orange-900/20 rounded-lg mr-3">
                  <Calendar className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Weekly Ad Spend</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white">
                    {formatCurrency(150 * 7)}
                  </p>
                  <p className="text-xs text-orange-600 dark:text-orange-400">per week</p>
                </div>
              </div>
            </div>
          </AnalyticsTooltip>

          {/* Monthly Ad Spend */}
          <AnalyticsTooltip content="Monthly Ad Spend - Total advertising spend per month. Calculated as daily spend × 30 days for consistent monthly comparison." id="monthly-ad-spend-tooltip">
            <div className="bg-white dark:bg-transparent backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg p-4 cursor-help">
              <div className="flex items-center">
                <div className="p-2 bg-yellow-100 dark:bg-yellow-900/20 rounded-lg mr-3">
                  <Activity className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Monthly Ad Spend</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white">
                    {formatCurrency(150 * 30)}
                  </p>
                  <p className="text-xs text-yellow-600 dark:text-yellow-400">per month</p>
                </div>
              </div>
            </div>
          </AnalyticsTooltip>

          {/* Annual Ad Spend */}
          <AnalyticsTooltip content="Annual Ad Spend - Total advertising spend per year. Calculated as daily spend × 365 days." id="annual-ad-spend-tooltip">
            <div className="bg-white dark:bg-transparent backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg p-4 cursor-help">
              <div className="flex items-center">
                <div className="p-2 bg-red-100 dark:bg-red-900/20 rounded-lg mr-3">
                  <DollarSign className="h-5 w-5 text-red-600 dark:text-red-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Annual Ad Spend</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white">
                    {formatCurrency(150 * 365)}
                  </p>
                  <p className="text-xs text-red-600 dark:text-red-400">per year</p>
                </div>
              </div>
            </div>
          </AnalyticsTooltip>
        </div>
      </div>

      {/* Fixed Expenses & Profit Analysis */}
      <div className="mb-6">
        <h3 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-3">Fixed Expenses & Profit Analysis</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
          {/* Fixed Monthly Expenses */}
          <AnalyticsTooltip content="Fixed Monthly Expenses - Recurring monthly costs including subscriptions, tools, and services. Currently set at $650/month." id="fixed-expenses-tooltip">
            <div className="bg-white dark:bg-transparent backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg p-4 cursor-help">
              <div className="flex items-center">
                <div className="p-2 bg-gray-100 dark:bg-gray-900/20 rounded-lg mr-3">
                  <CreditCard className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Fixed Expenses</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white">
                    {formatCurrency(650)}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">per month</p>
                </div>
              </div>
            </div>
          </AnalyticsTooltip>

          {/* Total Monthly Expenses */}
          <AnalyticsTooltip content="Total Monthly Expenses - Combined monthly ad spend and fixed expenses. This is your total monthly operational cost." id="total-monthly-expenses-tooltip">
            <div className="bg-white dark:bg-transparent backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg p-4 cursor-help">
              <div className="flex items-center">
                <div className="p-2 bg-red-100 dark:bg-red-900/20 rounded-lg mr-3">
                  <Minus className="h-5 w-5 text-red-600 dark:text-red-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Total Monthly Expenses</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white">
                    {formatCurrency(totalMonthlyExpenses)}
                  </p>
                  <p className="text-xs text-red-600 dark:text-red-400">ads + fixed costs</p>
                </div>
              </div>
            </div>
          </AnalyticsTooltip>

          {/* Monthly Profit */}
          <AnalyticsTooltip content="Monthly Profit - Net profit calculated as MRR minus total monthly expenses (ad spend + fixed costs). This shows your actual monthly profitability." id="monthly-profit-tooltip">
            <div className="bg-white dark:bg-transparent backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg p-4 cursor-help">
              <div className="flex items-center">
                <div className={`p-2 rounded-lg mr-3 ${
                  monthlyProfit >= 0 
                    ? 'bg-green-100 dark:bg-green-900/20' 
                    : 'bg-red-100 dark:bg-red-900/20'
                }`}>
                  <TrendingUp className={`h-5 w-5 ${
                    monthlyProfit >= 0 
                      ? 'text-green-600 dark:text-green-400' 
                      : 'text-red-600 dark:text-red-400'
                  }`} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Monthly Profit</p>
                  <p className={`text-xl font-bold ${
                    monthlyProfit >= 0 
                      ? 'text-green-600 dark:text-green-400' 
                      : 'text-red-600 dark:text-red-400'
                  }`}>
                    {formatCurrency(monthlyProfit)}
                  </p>
                  <p className={`text-xs ${
                    monthlyProfit >= 0 
                      ? 'text-green-600 dark:text-green-400' 
                      : 'text-red-600 dark:text-red-400'
                  }`}>
                    {monthlyProfit >= 0 ? 'profitable' : 'loss'}
                  </p>
                </div>
              </div>
            </div>
          </AnalyticsTooltip>

          {/* Annual Profit */}
          <AnalyticsTooltip content="Annual Profit - Net annual profit calculated as ARR minus total annual expenses (annual ad spend + annual fixed costs). This shows your projected yearly profitability." id="annual-profit-tooltip">
            <div className="bg-white dark:bg-transparent backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg p-4 cursor-help">
              <div className="flex items-center">
                <div className={`p-2 rounded-lg mr-3 ${
                  annualProfit >= 0 
                    ? 'bg-green-100 dark:bg-green-900/20' 
                    : 'bg-red-100 dark:bg-red-900/20'
                }`}>
                  <Activity className={`h-5 w-5 ${
                    annualProfit >= 0 
                      ? 'text-green-600 dark:text-green-400' 
                      : 'text-red-600 dark:text-red-400'
                  }`} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Annual Profit</p>
                  <p className={`text-xl font-bold ${
                    annualProfit >= 0 
                      ? 'text-green-600 dark:text-green-400' 
                      : 'text-red-600 dark:text-red-400'
                  }`}>
                    {formatCurrency(annualProfit)}
                  </p>
                  <p className={`text-xs ${
                    annualProfit >= 0 
                      ? 'text-green-600 dark:text-green-400' 
                      : 'text-red-600 dark:text-red-400'
                  }`}>
                    {annualProfit >= 0 ? 'profitable' : 'loss'}
                  </p>
                </div>
              </div>
            </div>
          </AnalyticsTooltip>
        </div>
      </div>

      {/* Break-Even Analysis */}
      <div className="mb-6">
        <h3 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-3">Break-Even Analysis</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
          {/* Users Needed for Break-Even */}
          <AnalyticsTooltip content="Users Needed for Break-Even - Number of additional $34/month users needed to reach monthly profitability (MRR > Total Monthly Expenses). Calculated as: (Total Monthly Expenses - Current MRR) ÷ $34." id="breakeven-users-tooltip">
            <div className="bg-white dark:bg-transparent backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg p-4 cursor-help">
              <div className="flex items-center">
                <div className={`p-2 rounded-lg mr-3 ${
                  monthlyProfit >= 0 
                    ? 'bg-green-100 dark:bg-green-900/20' 
                    : 'bg-orange-100 dark:bg-orange-900/20'
                }`}>
                  <Target className={`h-5 w-5 ${
                    monthlyProfit >= 0 
                      ? 'text-green-600 dark:text-green-400' 
                      : 'text-orange-600 dark:text-orange-400'
                  }`} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Users Needed for Break-Even</p>
                  <p className={`text-xl font-bold ${
                    monthlyProfit >= 0 
                      ? 'text-green-600 dark:text-green-400' 
                      : 'text-orange-600 dark:text-orange-400'
                  }`}>
                    {monthlyProfit >= 0 ? '0' : Math.ceil(Math.abs(monthlyProfit) / 34).toLocaleString()}
                  </p>
                  <p className={`text-xs ${
                    monthlyProfit >= 0 
                      ? 'text-green-600 dark:text-green-400' 
                      : 'text-orange-600 dark:text-orange-400'
                  }`}>
                    {monthlyProfit >= 0 ? 'already profitable!' : 'at $34/month each'}
                  </p>
                </div>
              </div>
            </div>
          </AnalyticsTooltip>

          {/* MRR Needed for Break-Even */}
          <AnalyticsTooltip content="MRR Needed for Break-Even - Total Monthly Recurring Revenue needed to cover all monthly expenses and reach profitability. This is your break-even point." id="breakeven-mrr-tooltip">
            <div className="bg-white dark:bg-transparent backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg p-4 cursor-help">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg mr-3">
                  <DollarSign className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-600 dark:text-gray-400">MRR Needed for Break-Even</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white">
                    {formatCurrency(totalMonthlyExpenses)}
                  </p>
                  <p className="text-xs text-blue-600 dark:text-blue-400">to cover all expenses</p>
                </div>
              </div>
            </div>
          </AnalyticsTooltip>

          {/* Progress to Break-Even */}
          <AnalyticsTooltip content="Progress to Break-Even - Percentage of the way to monthly profitability. Shows how close you are to covering all monthly expenses with recurring revenue." id="breakeven-progress-tooltip">
            <div className="bg-white dark:bg-transparent backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg p-4 cursor-help">
              <div className="flex items-center">
                <div className="p-2 bg-purple-100 dark:bg-purple-900/20 rounded-lg mr-3">
                  <BarChart3 className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Progress to Break-Even</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white">
                    {Math.min((currentMRR / totalMonthlyExpenses) * 100, 100).toFixed(1)}%
                  </p>
                  <p className="text-xs text-purple-600 dark:text-purple-400">of break-even MRR</p>
                </div>
              </div>
            </div>
          </AnalyticsTooltip>

          {/* Total Users Needed for Break-Even */}
          <AnalyticsTooltip content="Total Users Needed for Break-Even - Total number of $34/month paying users needed to reach monthly profitability. This is your target customer count (current users + additional needed)." id="total-breakeven-users-tooltip">
            <div className="bg-white dark:bg-transparent backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg p-4 cursor-help">
              <div className="flex items-center">
                <div className={`p-2 rounded-lg mr-3 ${
                  monthlyProfit >= 0 
                    ? 'bg-green-100 dark:bg-green-900/20' 
                    : 'bg-blue-100 dark:bg-blue-900/20'
                }`}>
                  <Users className={`h-5 w-5 ${
                    monthlyProfit >= 0 
                      ? 'text-green-600 dark:text-green-400' 
                      : 'text-blue-600 dark:text-blue-400'
                  }`} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Total Users for Break-Even</p>
                  <p className={`text-xl font-bold ${
                    monthlyProfit >= 0 
                      ? 'text-green-600 dark:text-green-400' 
                      : 'text-blue-600 dark:text-blue-400'
                  }`}>
                    {(() => {
                      const currentPayingUsers = analyticsData.summary?.payingCustomers || 0;
                      if (monthlyProfit >= 0) return currentPayingUsers;
                      const additionalUsersNeeded = Math.ceil(Math.abs(monthlyProfit) / 34);
                      return currentPayingUsers + additionalUsersNeeded;
                    })()}
                  </p>
                  <p className={`text-xs ${
                    monthlyProfit >= 0 
                      ? 'text-green-600 dark:text-green-400' 
                      : 'text-blue-600 dark:text-blue-400'
                  }`}>
                    {monthlyProfit >= 0 ? 'already profitable' : `(have ${analyticsData.summary?.payingCustomers || 0})`}
                  </p>
                </div>
              </div>
            </div>
          </AnalyticsTooltip>
        </div>
      </div>
    </>
  );
};

export default ExpensesMetrics;


